"use client";

import { Wrench } from "lucide-react";
import * as React from "react";
import type { JSX } from "react/jsx-runtime";
import { Button } from "@/components/ui/atoms/button";
import { Card, CardContent } from "@/components/ui/atoms/card";
import { Checkbox } from "@/components/ui/atoms/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/atoms/collapsible";
import { useIsTruncated } from "@/hooks/use-is-truncated";
import { cn } from "@/lib/utils";

export type ToolCardProps = {
  fully_qualified_name: string;
  name?: string;
  toolkitName?: string;
  description?: string;
  isSelected?: boolean;
  Icon?: (props: { className?: string }) => JSX.Element;
  onSelect?: (checked: boolean) => void;
  hideCheckbox?: boolean;
} & Omit<React.HTMLAttributes<HTMLDivElement>, "onSelect">;

function ToolCardImpl({
  fully_qualified_name,
  name,
  toolkitName,
  description,
  Icon = (p) => <Wrench {...p} />,
  isSelected = false,
  onSelect,
  hideCheckbox = false,
  ...rest
}: ToolCardProps) {
  const toggle = React.useCallback(
    () => onSelect?.(!isSelected),
    [isSelected, onSelect]
  );

  const [isExpanded, setIsExpanded] = React.useState(false);
  const { ref: descriptionRef, isTruncated } = useIsTruncated(!isExpanded);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    const isEnter = e.key === "Enter" || e.code === "Enter";
    const isSpace = e.key === " " || e.key === "Spacebar" || e.code === "Space";
    if (isEnter || isSpace) {
      e.preventDefault();
      toggle();
    }
  };

  const handleCheckboxChange = React.useCallback(
    (checked: boolean | "indeterminate") => onSelect?.(!!checked),
    [onSelect]
  );

  const stopPropagationClick = React.useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
  }, []);
  const stopPropagationKeyDown = React.useCallback((e: React.KeyboardEvent) => {
    const isEnter = e.key === "Enter" || e.code === "Enter";
    const isSpace = e.key === " " || e.key === "Spacebar" || e.code === "Space";
    if (isEnter || isSpace) {
      e.stopPropagation();
    }
  }, []);

  return (
    <Card
      {...rest}
      className={cn(
        "group relative cursor-pointer overflow-hidden rounded-lg border py-3 transition-all duration-200",
        "bg-white/50 hover:bg-white/70 dark:bg-neutral-900/50 dark:hover:bg-neutral-900/70",
        "hover:-translate-y-0.5 hover:shadow-lg",
        isSelected && "border-2 border-emerald-600 dark:border-emerald-400",
        !isSelected &&
          "border-neutral-200 hover:border-neutral-300 dark:border-neutral-800 dark:hover:border-neutral-700"
      )}
      onClick={(e) => {
        if (
          // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
          (e.target as HTMLElement).closest('[data-slot="checkbox-wrapper"]')
        ) {
          return;
        }
        toggle();
      }}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <CardContent className="px-3">
        <div
          className={cn(
            "grid items-start gap-2",
            "grid-cols-[20px_1fr_auto]",
            hideCheckbox && "grid-cols-[1fr_auto]"
          )}
        >
          {!hideCheckbox && (
            <div
              className="flex items-center justify-center pt-1.5"
              data-slot="checkbox-wrapper"
            >
              <Checkbox
                aria-label={`Select ${fully_qualified_name}`}
                checked={isSelected}
                onCheckedChange={handleCheckboxChange}
                tabIndex={-1}
              />
            </div>
          )}

          {/* Main content */}
          <div className="min-w-0 space-y-2">
            {/* Row 1: Tool name and toolkit name */}
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex size-9 items-center justify-center rounded bg-gradient-to-b from-neutral-200 to-neutral-100 ring-1 ring-black/5 dark:from-neutral-800 dark:to-neutral-900 dark:ring-white/5">
                <Icon className="size-6 text-neutral-700 dark:text-neutral-200" />
              </div>
              <div className="flex h-9 min-w-0 flex-col justify-center space-y-0.5">
                <h3 className="truncate font-semibold text-base text-neutral-900 leading-tight dark:text-neutral-100">
                  {name ?? fully_qualified_name}
                </h3>
                {toolkitName && (
                  <span className="font-medium text-neutral-500 text-xs leading-tight dark:text-neutral-400">
                    {toolkitName}
                  </span>
                )}
              </div>
            </div>

            {/* Row 2: Description */}
            {description && (
              <Collapsible onOpenChange={setIsExpanded} open={isExpanded}>
                <div className="space-y-1">
                  <p
                    className={cn(
                      "text-neutral-600 text-sm dark:text-neutral-400",
                      !isExpanded && "line-clamp-3"
                    )}
                    ref={descriptionRef}
                  >
                    {description}
                  </p>
                  {isTruncated && (
                    <CollapsibleTrigger
                      render={
                        <Button
                          className="h-auto px-0"
                          onClick={stopPropagationClick}
                          onKeyDown={stopPropagationKeyDown}
                          size="sm"
                          variant="link"
                        />
                      }
                    >
                      <span>{isExpanded ? "Show less" : "Show more"}</span>
                    </CollapsibleTrigger>
                  )}
                </div>
                <CollapsibleContent />
              </Collapsible>
            )}
          </div>

          {/* Right space for alignment */}
          <div className="w-8" />
        </div>
      </CardContent>
    </Card>
  );
}

import { createPropsComparator } from "@/components/ui/utils/memo";

const toolCardPropsComparator = createPropsComparator<ToolCardProps>([
  "fully_qualified_name",
  "name",
  "toolkitName",
  "description",
  "isSelected",
  "Icon",
  "hideCheckbox",
]);

export const ToolCard = React.memo(ToolCardImpl, toolCardPropsComparator);
